import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Heart } from "lucide-react";
import { useLang } from "@/i18n";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Chip } from "@/components/game/Palette";
import { CreationCard } from "@/components/game/CreationCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GAMES = ["tablero", "hamburguesa", "pizza", "retos"];
const PLAY = { tablero: "/tablero", hamburguesa: "/hamburguesa", pizza: "/pizza", retos: "/retos" };

export default function Galeria() {
  const { t, lang } = useLang();
  const s = t.gallery;
  const [items, setItems] = useState(null);
  const [game, setGame] = useState(null);
  const [sort, setSort] = useState("recent");

  useEffect(() => {
    axios.get(`${API}/creations?limit=120`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const onLike = (id, likes) => setItems((xs) => xs.map((c) => (c.id === id ? { ...c, likes } : c)));
  const visible = (items || []).filter((c) => !game || c.game === game)
    .sort((a, b) => (sort === "likes" ? (b.likes || 0) - (a.likes || 0) || b.created_at.localeCompare(a.created_at) : b.created_at.localeCompare(a.created_at)));

  return (
    <div data-testid="gallery-page" className="pt-16 min-h-screen">
      <Seo page="galeria" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-[#C86A4B] font-bold mb-3">{s.kicker}</p>
          <h1 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#132A1D] tracking-tight">{s.pageTitle}</h1>
          <p className="text-[#5C5C5C] mt-4 max-w-xl leading-relaxed">{s.pageSub}</p>
        </Reveal>
        <div className="flex flex-wrap gap-2 mt-10 mb-10" data-testid="gallery-filters">
          <Chip testid="gallery-filter-all" onClick={() => setGame(null)} active={!game}>{s.all}</Chip>
          {GAMES.map((k) => <Chip key={k} testid={`gallery-filter-${k}`} onClick={() => setGame(k)} active={game === k}>{s.games[k]}</Chip>)}
          <span className="w-px bg-black/10 mx-1 hidden sm:block" />
          <Chip testid="gallery-sort-recent" onClick={() => setSort("recent")} active={sort === "recent"} extra={sort === "recent" ? "!bg-[#C86A4B]" : ""}>{s.recent}</Chip>
          <Chip testid="gallery-sort-likes" onClick={() => setSort("likes")} active={sort === "likes"} extra={sort === "likes" ? "!bg-[#C86A4B]" : ""}><Heart size={13} /> {s.mostLoved}</Chip>
        </div>
        {items && visible.length === 0 && (
          <div data-testid="gallery-empty" className="bg-[#EFECE6] rounded-2xl p-10 text-center">
            <p className="font-serif-d text-2xl text-[#132A1D]">{s.empty}</p>
            <Link to="/tablero" className="inline-flex items-center gap-2 mt-5 rounded-full bg-[#2E5A3B] text-white px-6 py-3 text-sm font-semibold hover:bg-[#C86A4B] transition-colors duration-300">{s.create} <ArrowRight size={16} /></Link>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-testid="gallery-grid">
          {visible.map((c, i) => (
            <Reveal key={c.id} delay={0.04 * (i % 8)}>
              <CreationCard c={c} onLike={onLike} />
            </Reveal>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 mt-14">
          {GAMES.map((k) => (
            <Link key={k} to={PLAY[k]} data-testid={`gallery-play-${k}`} className="inline-flex items-center gap-2 rounded-full border border-black/15 text-[#132A1D] px-5 py-2.5 text-sm font-semibold hover:bg-[#EFECE6] transition-colors duration-300">
              {s.playIn} {s.games[k]} <ArrowRight size={14} />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
